import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { publicTicketSalesAPI, membershipPaymentAPI, membershipInvoiceAPI } from '../../../../services/membershipApi';
import TicketCard from '../TicketCard';
import { Icon } from '../../Common/Icons';
import { PublicShell, EventHero, PublicMessage, inputCls, fmtMoney, fmtDateTime } from './publicTicketShared';

const POLL_MS = 4000;
const SLIP_MAX = 5 * 1024 * 1024;
const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .ticket-print, .ticket-print * { visibility: visible !important; }
  .ticket-print { position: absolute; left: 0; top: 0; width: 100%; padding: 12mm; }
  .ticket-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;
const METHODS = [
  { key: 'online', flag: 'online', icon: 'credit-card', label: 'Pay online' },
  { key: 'bank_transfer', flag: 'bankTransfer', icon: 'building', label: 'Bank transfer' },
  { key: 'cashier', flag: 'cashier', icon: 'cash', label: 'At the cashier' },
];

const CopyValue = ({ value }) => {
  const [done, setDone] = useState(false);
  return (
    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* copy not allowed */ } }}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-violet-700 hover:bg-violet-50">
      <Icon name={done ? 'check' : 'copy'} className="h-3.5 w-3.5" />{done ? 'Copied' : 'Copy'}
    </button>
  );
};

/** Pay online: hand over to the payment gateway, which returns here. */
const OnlinePay = ({ order, saleToken, orderToken, autoStart }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const started = useRef(false);
  const start = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const path = `/membership/tickets/${saleToken}/order/${orderToken}`;
      const r = await membershipPaymentAPI.initiatePublicCard(order.invoice.payToken, {
        returnUrl: `${window.location.origin}${path}`, cancelUrl: `${window.location.origin}${path}`, returnPath: path,
      });
      const url = r.data?.data?.paymentPageUrl || r.data?.data?.paymentUrl;
      if (!url) throw new Error('No payment page');
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.message || 'The payment gateway could not be reached. Please try again, or choose another way to pay.');
      setBusy(false);
    }
  }, [order.invoice.payToken, saleToken, orderToken]);
  useEffect(() => { if (autoStart && !started.current) { started.current = true; start(); } }, [autoStart, start]);
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">You will go to the bank's secure page to enter your card. Your ticket appears here as soon as the payment goes through.</p>
      {error && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
      <button type="button" onClick={start} disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-6 py-4 text-base font-semibold text-white shadow-xl shadow-violet-600/25 hover:bg-violet-700 disabled:opacity-60">
        <Icon name="lock" className="h-5 w-5" />{busy ? 'Opening the secure payment page' : `Pay ${fmtMoney(order.invoice.total, order.invoice.currency)} securely`}
      </button>
      <p className="flex items-center justify-center gap-3 text-xs text-gray-400"><span>VISA</span><span>Mastercard</span><span>3-D Secure</span></p>
    </div>
  );
};

/** Bank transfer: account details, then upload the slip. */
const BankPay = ({ order, onSubmitted }) => {
  const [file, setFile] = useState(null);
  const [reference, setReference] = useState('');
  const [bank, setBank] = useState(order.bankAccounts[0]?.bankName || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (order.awaitingApproval) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <p className="flex items-center gap-2 font-semibold text-amber-900"><Icon name="clock" className="h-5 w-5" />Slip received</p>
        <p className="mt-1 text-sm text-amber-800">SLIM finance is checking your transfer. Your ticket with its QR code is emailed to {order.ticket.email} and shown here once it is confirmed.</p>
      </div>
    );
  }
  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (!file) return setError('Attach your bank slip (photo or PDF).');
    if (file.size > SLIP_MAX) return setError('The slip must be 5 MB or smaller.');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('receipt', file); fd.append('paymentMethod', 'bank_transfer'); fd.append('bankName', bank); fd.append('referenceNumber', reference);
      await membershipInvoiceAPI.submitPublicBankTransfer(order.invoice.payToken, fd);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not upload the slip. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-600">Transfer <span className="font-semibold text-gray-900">{fmtMoney(order.invoice.total, order.invoice.currency)}</span> to one of these accounts. Use <span className="font-mono font-semibold text-gray-900">{order.invoice.invoiceNumber}</span> as the reference.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {order.bankAccounts.map((b) => (
          <div key={`${b.bankName}-${b.accountNumber}`} className="rounded-xl border border-gray-200 bg-gray-50 p-3.5 text-sm">
            <p className="font-semibold text-gray-900">{b.bankName}</p>
            {b.branchName && <p className="text-xs text-gray-500">{b.branchName} branch</p>}
            <p className="mt-1.5 text-gray-600">{b.accountName}</p>
            <p className="flex items-center justify-between gap-2 font-mono text-base font-semibold text-gray-900">{b.accountNumber}<CopyValue value={b.accountNumber} /></p>
          </div>
        ))}
        {!order.bankAccounts.length && <p className="text-sm text-gray-500">Please contact SLIM for the bank account details.</p>}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {order.bankAccounts.length > 1 && (
          <label className="block text-sm"><span className="mb-1 block font-medium text-gray-700">Paid to</span>
            <select value={bank} onChange={(e) => setBank(e.target.value)} className={inputCls}>{order.bankAccounts.map((b) => <option key={b.accountNumber}>{b.bankName}</option>)}</select>
          </label>
        )}
        <label className="block text-sm"><span className="mb-1 block font-medium text-gray-700">Transfer reference (optional)</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls} placeholder="From your bank slip" />
        </label>
      </div>
      <label className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition ${file ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/40'}`}>
        <Icon name={file ? 'check-circle' : 'paperclip'} className={`h-7 w-7 ${file ? 'text-emerald-600' : 'text-gray-400'}`} />
        <span className="text-sm font-medium text-gray-800">{file ? file.name : 'Upload your bank slip'}</span>
        <span className="text-xs text-gray-500">JPG, PNG or PDF, up to 5 MB</span>
        <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      {error && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
      <button type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-violet-600/25 hover:bg-violet-700 disabled:opacity-60">
        {busy ? 'Uploading' : 'Submit bank slip'}
      </button>
    </form>
  );
};

/** Pay at the cashier: the reference to show. */
const CashierPay = ({ order }) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600">Pay by cash or card at the SLIM cashier. Show this reference; your ticket is emailed to {order.ticket.email} as soon as the payment is recorded.</p>
    <div className="grid grid-cols-1 gap-3 rounded-2xl bg-gray-900 p-5 text-white sm:grid-cols-3">
      <div><p className="text-[0.6875rem] uppercase tracking-wider text-white/60">Invoice</p><p className="font-mono text-lg font-semibold">{order.invoice.invoiceNumber}</p></div>
      <div><p className="text-[0.6875rem] uppercase tracking-wider text-white/60">Ticket</p><p className="font-mono text-lg font-semibold">{order.ticket.ticketNumber}</p></div>
      <div><p className="text-[0.6875rem] uppercase tracking-wider text-white/60">Amount</p><p className="text-lg font-semibold">{fmtMoney(order.invoice.total, order.invoice.currency)}</p></div>
    </div>
    {order.holdUntil && <p className="flex items-center gap-1.5 text-xs text-amber-700"><Icon name="clock" className="h-4 w-4" />Your ticket is held until {fmtDateTime(order.holdUntil)}. Unpaid orders are released after that.</p>}
  </div>
);

/**
 * The buyer's own order page (/membership/tickets/:token/order/:orderToken): pay (online, bank
 * slip or cashier), follow the status, and get the ticket with its QR code once paid.
 */
const PublicTicketOrder = () => {
  const { token: saleToken, orderToken } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [method, setMethod] = useState('');
  const [switching, setSwitching] = useState(false);
  const gateway = params.get('paycorp'); // success | failed | cancelled, after the card page

  const load = useCallback(() => publicTicketSalesAPI.getOrder(orderToken)
    .then((r) => { const d = r.data?.data; setOrder(d); setMethod((m) => m || d.paymentMethod); setError(''); return d; })
    .catch((err) => { setError(err.response?.data?.message || 'Could not load your order.'); return null; }), [orderToken]);

  useEffect(() => { load(); }, [load]);
  // Keep checking while payment is outstanding (gateway result, cashier, slip approval)
  useEffect(() => {
    if (!order || order.status !== 'pending_payment') return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [order, load]);

  const chooseMethod = async (m) => {
    if (m === method) return;
    setMethod(m);
    setSwitching(true);
    try { const r = await publicTicketSalesAPI.setPaymentMethod(orderToken, m); setOrder(r.data?.data); } catch { /* keep the local choice; the page still works */ } finally { setSwitching(false); }
  };

  if (error && !order) return <PublicShell><PublicMessage icon="x-circle" tone="rose" title="Order not found">{error}</PublicMessage></PublicShell>;
  if (!order) return <PublicShell><div className="h-96 animate-pulse rounded-3xl bg-gray-100" /></PublicShell>;

  const { event, template, ticket } = order;
  const ready = order.status === 'issued' || order.status === 'checked_in';
  // event title / venue / time already resolved by the server (event details > template > event)
  const data = {
    eventTitle: event?.title, date: event?.startDate, venue: event?.venue, timeText: event?.timeText, holderName: ticket.holderName, membershipId: ticket.membershipId,
    allocationName: ticket.allocationName, price: ticket.price, currency: ticket.currency, ticketNumber: ticket.ticketNumber,
  };
  const confirming = !ready && gateway === 'success';

  return (
    <PublicShell>
      <style>{PRINT_CSS}</style>
      <div className="space-y-6">
        <div className="print:hidden"><EventHero event={event} template={template} compact /></div>

        {ready ? (
          <div className="mx-auto max-w-3xl space-y-5">
            <div className="flex items-start gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 print:hidden">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Icon name="check-circle" className="h-7 w-7" /></span>
              <div>
                <p className="text-lg font-semibold text-emerald-900">{order.status === 'checked_in' ? 'Ticket used: you are checked in' : "You're all set!"}</p>
                <p className="text-sm text-emerald-800">Show this QR code at the entrance, on your phone or printed. A copy has been emailed to {ticket.email}.</p>
              </div>
            </div>
            <div className="ticket-print">
              <TicketCard vertical template={template} data={data} qrDataUrl={order.qrDataUrl} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4 print:hidden">
              <div className="rounded-xl bg-white p-3 ring-1 ring-gray-100"><p className="text-xs text-gray-500">Ticket</p><p className="font-mono font-semibold">{ticket.ticketNumber}</p></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-gray-100"><p className="text-xs text-gray-500">Holder</p><p className="truncate font-semibold">{ticket.holderName}</p></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-gray-100"><p className="text-xs text-gray-500">{ticket.holderType === 'member' ? 'Membership ID' : 'Mobile'}</p><p className="truncate font-semibold">{ticket.holderType === 'member' ? ticket.membershipId : ticket.mobile}</p></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-gray-100"><p className="text-xs text-gray-500">Paid</p><p className="font-semibold">{fmtMoney(ticket.price, ticket.currency)}</p></div>
            </div>
            <div className="flex flex-wrap justify-center gap-3 print:hidden">
              <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-600/25 hover:bg-violet-700">
                <Icon name="printer" className="h-4 w-4" />Print / save as PDF
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 print:hidden">Keep this page link private: anyone with it can see your ticket.</p>
          </div>
        ) : order.status === 'cancelled' ? (
          <PublicMessage icon="x-circle" tone="amber" title="This order is no longer active">
            <p>{order.cancelReason === 'Unpaid order expired' ? 'It was released because the payment was not completed in time.' : order.cancelReason}</p>
            <Link to={`/membership/tickets/${saleToken}`} className="mt-4 inline-flex rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">Buy a ticket again</Link>
          </PublicMessage>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:items-start">
            <div className="space-y-5 lg:col-span-3">
              {confirming ? (
                <div className="flex items-center gap-3 rounded-2xl border border-violet-200 bg-violet-50 p-5">
                  <span className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
                  <p className="text-sm text-violet-900">Payment received by the bank. Confirming it and issuing your ticket. This takes a few seconds.</p>
                </div>
              ) : (gateway === 'failed' || gateway === 'cancelled') && (
                <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{gateway === 'cancelled' ? 'The card payment was cancelled.' : 'The card payment did not go through.'} No money was taken. Try again or choose another way to pay.</p>
              )}

              <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-base font-semibold text-gray-900">Complete your payment</h2>
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Awaiting payment</span>
                </div>
                {order.invoice?.payToken ? (
                  <>
                    <div className="mb-5 grid grid-cols-3 gap-2 rounded-2xl bg-gray-100 p-1" role="tablist">
                      {METHODS.filter((m) => order.payment?.[m.flag]).map((m) => (
                        <button key={m.key} type="button" role="tab" aria-selected={method === m.key} onClick={() => chooseMethod(m.key)} disabled={switching}
                          className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2.5 text-xs font-semibold transition sm:flex-row sm:justify-center sm:text-sm ${method === m.key ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                          <Icon name={m.icon} className="h-4 w-4" />{m.label}
                        </button>
                      ))}
                    </div>
                    {method === 'online' && <OnlinePay order={order} saleToken={saleToken} orderToken={orderToken} autoStart={!!location.state?.autoPay && !gateway} />}
                    {method === 'bank_transfer' && <BankPay order={order} onSubmitted={load} />}
                    {method === 'cashier' && <CashierPay order={order} />}
                  </>
                ) : (
                  <p className="text-sm text-gray-600">Your payment is being processed. This page updates on its own.</p>
                )}
              </section>
            </div>
            <aside className="space-y-4 lg:sticky lg:top-6 lg:col-span-2">
              <div className="rounded-2xl bg-white p-3 shadow-sm ring-1 ring-gray-100 sm:p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">Your ticket</p>
                <TicketCard vertical template={template} data={data} qrLabel="Your QR code appears once paid" />
              </div>
              <div className="rounded-2xl bg-white p-5 text-sm shadow-sm ring-1 ring-gray-100">
                <p className="font-semibold text-gray-900">Order details</p>
                <dl className="mt-3 space-y-2 text-gray-600">
                  <div className="flex justify-between gap-3"><dt>Name</dt><dd className="truncate font-medium text-gray-900">{ticket.holderName}</dd></div>
                  {ticket.membershipId && <div className="flex justify-between gap-3"><dt>Membership ID</dt><dd className="font-medium text-gray-900">{ticket.membershipId}</dd></div>}
                  <div className="flex justify-between gap-3"><dt>Email</dt><dd className="truncate font-medium text-gray-900">{ticket.email}</dd></div>
                  <div className="flex justify-between gap-3"><dt>Invoice</dt><dd className="font-mono font-medium text-gray-900">{order.invoice?.invoiceNumber}</dd></div>
                  <div className="flex justify-between gap-3 border-t border-gray-100 pt-2 text-base font-bold text-gray-900"><dt>Total</dt><dd>{fmtMoney(order.invoice?.total, order.invoice?.currency)}</dd></div>
                </dl>
              </div>
            </aside>
          </div>
        )}
      </div>
    </PublicShell>
  );
};

export default PublicTicketOrder;
